import { child, onChildAdded, set } from 'firebase/database'
import dayjs from 'dayjs'
import { useRealtimeDatabase } from '~/composables/useRealtimeDatabase'
import { usePosShopStore, type RunMode } from '~/stores/pos.shop'
import printJob from '~/utils/printers/printJobToKitchen'
import { claimJobPrint } from '~/utils/printers/printOnce'

const MAX_AGE_MINUTES = 10

export default defineNuxtPlugin(() => {
  const shopStore = usePosShopStore()

  // runMode is null at plugin-init time — it is loaded asynchronously from
  // localStorage in bootstrap (loadRunMode). Reading it once synchronously here
  // always saw null and silently skipped both branches. Watch it instead and
  // initialise the correct side the moment it resolves. Init each side at most
  // once; switching runMode requires a reload anyway.
  let initialised: RunMode = null

  watch(
    () => shopStore.runMode,
    (runMode) => {
      if (!runMode || runMode === initialised) return

      if (runMode === '2nd_cashier') {
        initialised = runMode
        initSecondCashierSender()
        return
      }

      if (runMode === 'cashier') {
        initialised = runMode
        initCashierReceiver()
      }
    },
    { immediate: true }
  )
})

function initSecondCashierSender() {
  const jobsStore = usePosJobsStore()
  jobsStore.$onAction(({ name, args, after }) => {
    if (name !== 'add') return
    const job = args[0]
    if (!job?.jobId) return
    if (job?.type !== 200) return

     
    after(() => {
      try {
     

        const ref = useRealtimeDatabase().secondCashierPrintRef()
        const key = String(job.jobId).replaceAll('.', '_')

         
        set(child(ref, key), {
          job,
          sentAt: dayjs().toISOString(),
        })
      } catch (error) {
        console.error('second-cashier: failed to push print request', error)
      }
    })
  })
}

function initCashierReceiver() {
  const ref = useRealtimeDatabase().secondCashierPrintRef()
  onChildAdded(ref, (snapshot) => {
    const payload = snapshot.val()
    const purge = () => set(snapshot.ref, null)

    if (!payload || !payload.job || !payload.sentAt) {
      setTimeout(purge,2000)
      return
    }

    const ageMin = dayjs().diff(dayjs(payload.sentAt), 'minute', true)
    if (!Number.isFinite(ageMin) || ageMin > MAX_AGE_MINUTES) {
      console.log('second-cashier: drop stale print job', { ageMin, jobId: payload.job?.jobId })
      setTimeout(purge, 2000)
      return
    }

    // Same idempotency mandate as the local add path: one job = one slip. This
    // listener attaches without clearing the node first and only purges 2s AFTER
    // printing, so a main-cashier reload replays every child still sitting there
    // (up to MAX_AGE_MINUTES old) and reprints it. Claim before printing.
    if (!claimJobPrint(payload.job?.jobId)) {
      console.log('second-cashier: skip reprint, jobId already printed', payload.job?.jobId)
      setTimeout(purge, 2000)
      return
    }

    try {
      printJob(payload.job)
    } catch (error) {
      console.error('second-cashier: printJob failed', error)
    } finally {
      setTimeout(purge, 2000)
    }
  })
}
